import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  ErsatzErgebnis,
  TokenKlartext,
  TokenPaketErgebnis,
  TokenUebersicht,
  Veranstaltung,
  VeranstaltungEingabe,
} from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';

/**
 * Anbindung der Veranstaltungs- und Tokenverwaltung (§6, §7, §8.1).
 *
 * Die Klartext-Ausgabe hat bewusst einen eigenen, deutlich benannten Weg: sie ist die einzige
 * Stelle, an der vollständige Tokens den Server verlassen (§7.9), und wird serverseitig
 * protokolliert.
 */

const BASIS = '/api/v1/admin/veranstaltungen';

export const VERANSTALTUNGEN_SCHLUESSEL = ['admin', 'veranstaltungen'] as const;

function veranstaltungSchluessel(id: string): readonly unknown[] {
  return [...VERANSTALTUNGEN_SCHLUESSEL, id];
}

function tokensSchluessel(id: string): readonly unknown[] {
  return [...veranstaltungSchluessel(id), 'tokens'];
}

export function useVeranstaltungen(
  mitArchivierten: boolean,
): UseQueryResult<Veranstaltung[], ApiFehler> {
  return useQuery<Veranstaltung[], ApiFehler>({
    queryKey: [...VERANSTALTUNGEN_SCHLUESSEL, { mitArchivierten }],
    queryFn: () =>
      apiAnfrage<Veranstaltung[]>(`${BASIS}${mitArchivierten ? '?archivierte=true' : ''}`),
  });
}

export function useVeranstaltung(id: string): UseQueryResult<Veranstaltung, ApiFehler> {
  return useQuery<Veranstaltung, ApiFehler>({
    queryKey: veranstaltungSchluessel(id),
    queryFn: () => apiAnfrage<Veranstaltung>(`${BASIS}/${id}`),
  });
}

export function useTokens(veranstaltungId: string): UseQueryResult<TokenUebersicht[], ApiFehler> {
  return useQuery<TokenUebersicht[], ApiFehler>({
    queryKey: tokensSchluessel(veranstaltungId),
    queryFn: () => apiAnfrage<TokenUebersicht[]>(`${BASIS}/${veranstaltungId}/tokens`),
  });
}

/**
 * Klartext-Ausgabe (§7.9).
 *
 * Bewusst **nicht** zwischengespeichert und nicht automatisch neu geladen: jeder Abruf erzeugt
 * einen Protokolleintrag, und vollständige Tokens sollen nicht länger als nötig im Speicher des
 * Browsers liegen.
 */
export function useTokenAusgabe(
  veranstaltungId: string,
  aktiv: boolean,
): UseQueryResult<TokenKlartext[], ApiFehler> {
  return useQuery<TokenKlartext[], ApiFehler>({
    queryKey: [...tokensSchluessel(veranstaltungId), 'ausgabe'],
    queryFn: () => apiAnfrage<TokenKlartext[]>(`${BASIS}/${veranstaltungId}/tokens/ausgabe`),
    enabled: aktiv,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

/** Pfad des CSV-Downloads. Der Browser lädt ihn unmittelbar; die Sitzung reist im Cookie mit. */
export function csvPfad(veranstaltungId: string): string {
  return `${BASIS}/${veranstaltungId}/tokens/ausgabe.csv`;
}

/**
 * Pfad der DIN-A6-Zugangskarten (§8.3).
 *
 * Wie beim CSV-Export ein gewöhnlicher Verweis: Die Datei enthält vollständige Tokens und wird
 * serverseitig erzeugt, aber nicht gespeichert (Entscheidung E22). Jeder Abruf wird protokolliert.
 */
export function kartenPfad(veranstaltungId: string): string {
  return `${BASIS}/${veranstaltungId}/tokens/karten.pdf`;
}

function useAktualisierend<TDaten, TVariablen>(
  fn: (variablen: TVariablen) => Promise<TDaten>,
): UseMutationResult<TDaten, ApiFehler, TVariablen> {
  const queryClient = useQueryClient();

  return useMutation<TDaten, ApiFehler, TVariablen>({
    mutationFn: fn,
    onSuccess: () => {
      // Statusänderungen wirken sich auf Liste, Einzelansicht und Tokenzahlen aus.
      void queryClient.invalidateQueries({ queryKey: VERANSTALTUNGEN_SCHLUESSEL });
    },
  });
}

export function useVeranstaltungAnlegen(): UseMutationResult<
  Veranstaltung,
  ApiFehler,
  VeranstaltungEingabe
> {
  return useAktualisierend((eingabe) =>
    apiAnfrage<Veranstaltung>(BASIS, { methode: 'POST', koerper: eingabe }),
  );
}

export function useVeranstaltungBearbeiten(
  id: string,
): UseMutationResult<Veranstaltung, ApiFehler, VeranstaltungEingabe> {
  return useAktualisierend((eingabe) =>
    apiAnfrage<Veranstaltung>(`${BASIS}/${id}`, { methode: 'PATCH', koerper: eingabe }),
  );
}

/** Zustandsändernde Aktionen nach §6.2, §7.2 und §19.2. */
export type VeranstaltungAktion =
  'code' | 'freigeben' | 'beenden' | 'archivieren' | 'dearchivieren' | 'duplizieren' | 'warteraum';

/**
 * Eine Aktion an der Veranstaltung.
 *
 * `erwarteterStatus` ist der Status, den die Oberfläche angezeigt hat. Er schützt vor allem das
 * endgültige Beenden vor einem veralteten Tab (§6.2, §25.3).
 */
export interface VeranstaltungAktionAnfrage {
  aktion: VeranstaltungAktion;
  erwarteterStatus?: string;
}

export function useVeranstaltungAktion(
  id: string,
): UseMutationResult<Veranstaltung, ApiFehler, VeranstaltungAktion | VeranstaltungAktionAnfrage> {
  return useAktualisierend((eingabe) => {
    const { aktion, erwarteterStatus } =
      typeof eingabe === 'string' ? { aktion: eingabe, erwarteterStatus: undefined } : eingabe;

    return apiAnfrage<Veranstaltung>(`${BASIS}/${id}/${aktion}`, {
      methode: 'POST',
      ...(erwarteterStatus === undefined ? {} : { koerper: { erwarteterStatus } }),
    });
  });
}

export function useVeranstaltungLoeschen(): UseMutationResult<void, ApiFehler, string> {
  return useAktualisierend((id) => apiAnfrage<void>(`${BASIS}/${id}`, { methode: 'DELETE' }));
}

export function useTokensErzeugen(
  veranstaltungId: string,
): UseMutationResult<TokenPaketErgebnis, ApiFehler, number> {
  return useAktualisierend((anzahl) =>
    apiAnfrage<TokenPaketErgebnis>(`${BASIS}/${veranstaltungId}/tokens`, {
      methode: 'POST',
      koerper: { anzahl },
    }),
  );
}

export function useTokenSperren(): UseMutationResult<TokenUebersicht, ApiFehler, string> {
  return useAktualisierend((tokenId) =>
    apiAnfrage<TokenUebersicht>(`/api/v1/admin/tokens/${tokenId}/sperren`, { methode: 'POST' }),
  );
}

/**
 * Ersetzt einen Token (§7.8).
 *
 * Die Antwort nennt den Stand des alten Tokens in einer laufenden Abstimmung — „bereits
 * abgestimmt" oder „noch nicht abgestimmt" —, damit die Oberfläche ihn vor einer Freigabe
 * anzeigen kann.
 */
export function useTokenErsetzen(): UseMutationResult<ErsatzErgebnis, ApiFehler, string> {
  return useAktualisierend((tokenId) =>
    apiAnfrage<ErsatzErgebnis>(`/api/v1/admin/tokens/${tokenId}/ersetzen`, { methode: 'POST' }),
  );
}

/**
 * Beendet nur die aktive Sitzung eines Teilnehmers (§9.8).
 *
 * Bewusst als **eigene** Aktion neben der Abmeldung: §9.8 nennt beide nebeneinander, weil sie
 * Verschiedenes bewirken. Hier bleibt der Teilnehmer anwesend, die berücksichtigte
 * Teilnehmerzahl ändert sich nicht, und er kann sich erneut anmelden.
 */
export function useTeilnehmerSitzungBeenden(): UseMutationResult<
  TokenUebersicht,
  ApiFehler,
  string
> {
  return useAktualisierend((tokenId) =>
    apiAnfrage<TokenUebersicht>(`/api/v1/admin/tokens/${tokenId}/sitzung-beenden`, {
      methode: 'POST',
    }),
  );
}

/** Meldet einen Teilnehmer vollständig ab (§9.8, §9.9). */
export function useTeilnehmerAbmelden(): UseMutationResult<TokenUebersicht, ApiFehler, string> {
  return useAktualisierend((tokenId) =>
    apiAnfrage<TokenUebersicht>(`/api/v1/admin/tokens/${tokenId}/abmelden`, { methode: 'POST' }),
  );
}
